// Firestore-backed lookup for in-flight/completed DFT and lattice jobs by
// candidate -- the durable half of job resume (see AppShell.jsx's
// DftAsyncPanel and LatticeWizard.jsx's dft_running resume effect).
//
// Before this module existed, localStorage (dftJobRegistry.js) was the ONLY
// source of truth for "is a job running for this candidate" -- losing it (a
// different browser, a cleared profile, private browsing, a wiped device)
// meant losing all visibility into an in-flight or even a completed job,
// with no way to recover it short of re-running an expensive GPAW/QE/GA
// job. dft-service, qe-service, and lattice-service each record uid +
// candidate_fingerprint on every job doc (models/dft-service/main.py's
// _init_job, models/qe-service/main.py's _init_job -- parity reached
// 2026-08-20 -- and models/lattice-service/jobs.py's create()); this module
// queries whichever collection the caller asks for. localStorage remains
// only the local-dev fallback, for when Firebase itself isn't configured.
//
// Firebase-unconfigured (local dev without a .env, firestoreDb is null) and
// any query error both resolve to an empty array, never a throw -- resuming
// a job is always best-effort, never something a caller has to wrap in its
// own try/catch.
import { collection, query, where, orderBy, limit as fsLimit, getDocs } from 'firebase/firestore';
import { auth, firestoreDb } from '../config/firebase';

// Newest-first, capped at 3: a resuming surface only ever cares about the
// most recent job(s) for this candidate (across every kind that writes to
// this collection) -- the composite index backing this query is (uid,
// candidate_fingerprint, submitted_at), not per-kind, so a caller that needs
// a SPECIFIC job kind (e.g. 'elastic_tensor') filters the returned docs
// itself (see each doc's payload.type for dftJobs, or request.objective for
// latticeJobs).
const MAX_RESULTS = 3;

// Returns up to MAX_RESULTS job docs for the signed-in user + candidate
// fingerprint, newest first by submitted_at. `collectionName` is the raw
// Firestore collection ('dftJobs', 'qeJobs', or 'latticeJobs'). Each
// returned object is the doc's data spread with `job_id` guaranteed present
// (falling back to the doc id if the field itself is somehow missing).
//
// `scope` (optional, from src/hooks/useCapability.js's useJobLookupScope):
// { orgId, viewOrg }. Org-scoped job ownership (2026-08-22): when the member
// holds jobs.view.org and has an org, look up by org_id so a TEAMMATE's
// in-flight or completed job for this candidate resumes too (backed by the
// (org_id, candidate_fingerprint, submitted_at) composite index); otherwise
// the original uid-scoped query. Legacy uid-only docs are only ever found by
// their owner, in both modes.
export async function findJobsForCandidate(collectionName, fingerprint, scope) {
  if (!firestoreDb || !auth?.currentUser?.uid || !collectionName || !fingerprint) return [];
  const byOrg = !!(scope && scope.viewOrg && scope.orgId);
  const ownerClause = byOrg ? where('org_id', '==', scope.orgId) : where('uid', '==', auth.currentUser.uid);
  try {
    const q = query(
      collection(firestoreDb, collectionName),
      ownerClause,
      where('candidate_fingerprint', '==', fingerprint),
      orderBy('submitted_at', 'desc'),
      fsLimit(MAX_RESULTS),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data() || {};
      return { ...data, job_id: data.job_id || d.id };
    });
  } catch (_) {
    // Missing composite index (a fresh/not-yet-deployed project), a
    // permission error, a network hiccup, Firestore being generally
    // unreachable: any of these must fall back to the localStorage
    // registry, never break the resuming surface.
    return [];
  }
}
